import { createContext, ReactNode, useContext, useState, useEffect } from 'react'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { getActivePlan, activePlan, cancelPlan } from '@/utilities/apphubApi'
import { getShopifyAdminAppUrl, redirectToUrl } from '@/utilities/misc'
import { Plan } from './types'
import { useAuth } from './AuthContext'
import { useMixpanel } from '@/hooks/useMixpanel'

type PlanProviderProps = {
  children: ReactNode
}

export type PlanContext = {
  currentShopPlan?: Plan
  activeSelectedPlan: (planId: string) => Promise<void>
  cancelCurrentPlan: () => Promise<void>
}

const PlanContext = createContext({} as PlanContext)

export function usePlan() {
  return useContext(PlanContext)
}

export function PlanProvider({ children }: PlanProviderProps) {
  const { currentShop } = useAuth()
  const testPayment = process.env.NODE_ENV !== 'production'
  const { setShopProps } = useMixpanel()

  const [shopPlans, setShopPlans] = useSessionStorage<Plan[]>('shop-plans', [])
  const [currentShopPlan, setCurrentShopPlan] = useState<Plan>()

  useEffect(() => {
    ;(async () => {
      if (!currentShop) return

      const plan = shopPlans.find((i) => i.shopifyDomain === currentShop.shopifyDomain)
      if (plan) {
        setCurrentShopPlan(plan)
      } else {
        const shopPlan = await getActivePlan(currentShop.upatraToken, testPayment)
        if (!shopPlan) {
          setShopProps({
            'Upatra Plan Id': undefined,
            'Plan Activated On': undefined,
            'Plan Name': undefined,
            'Plan Price': undefined,
          })

          return
        }

        setShopPlan({
          shopifyDomain: currentShop.shopifyDomain,
          name: shopPlan.name,
          price: shopPlan.price,
          activatedOn: shopPlan.activatedOn,
          upatraPlanId: shopPlan.upatraPlanId,
        })
        setCurrentShopPlan(shopPlan)

        // Hmm find a way to make it cleaner
        setShopProps({
          'Upatra Plan Id': shopPlan.upatraPlanId,
          'Plan Activated On': shopPlan.activatedOn,
          'Plan Name': shopPlan.name,
          'Plan Price': shopPlan.price,

          'Last Upatra Plan Id': shopPlan.upatraPlanId,
          'Last Plan Activated On': shopPlan.activatedOn,
          'Last Plan Name': shopPlan.name,
          'Last Plan Price': shopPlan.price,
        })
      }
    })()
  }, [currentShop])

  const setShopPlan = (shopPlan: Plan) => {
    const plan = shopPlans.find((i) => i.shopifyDomain === shopPlan.shopifyDomain)

    setShopPlans((currPlans) => {
      if (!plan) {
        return [...currPlans, { ...shopPlan }]
      } else {
        return currPlans.map((p) => {
          if (p.shopifyDomain === shopPlan.shopifyDomain)
            return {
              ...p,
              name: shopPlan.name,
              price: shopPlan.price,
              activatedOn: shopPlan.activatedOn,
              upatraPlanId: shopPlan.upatraPlanId,
            }
          else return p
        })
      }
    })
  }

  const activeSelectedPlan = async (planId: string) => {
    if (!currentShop) return

    const returnUrl = `${getShopifyAdminAppUrl(currentShop.shopifyDomain)}/plan`
    const confirmationUrl = await activePlan(
      currentShop.upatraToken,
      planId,
      returnUrl,
      testPayment
    )
    redirectToUrl(confirmationUrl)
  }

  const cancelCurrentPlan = async () => {
    if (!currentShopPlan || !currentShop) return

    await cancelPlan(currentShop.upatraToken)
    setCurrentShopPlan(undefined)
    setShopPlans((currPlans) => {
      return currPlans.filter((p) => p.upatraPlanId != currentShopPlan.upatraPlanId)
    })
  }

  return (
    <PlanContext.Provider
      value={{
        currentShopPlan,
        activeSelectedPlan,
        cancelCurrentPlan,
      }}
    >
      {children}
    </PlanContext.Provider>
  )
}
